import { useState } from 'react';
import { Pill, PillGroup } from '@/components/ui/Pill';
import { InsightCard } from '@/components/ui/Feedback';
import type { GroupRound, MatchResult, PlayerStanding, TeamId, TeamStanding } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { fmtDecimal, fmtOrdinal, fmtToPar } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import styles from './GroupStandings.module.css';

/**
 * O quadro da volta em grupo (canvas 1t).
 *
 * A frase que organiza a tela: **"Net é o resultado, bruto é o registro."** O quadro ordena pelo
 * que estava em disputa, mas mostra as tacadas ao lado — sem isso o número perde o sentido. O
 * seletor no topo é a mesma escolha feita no registro (1r), e a linha embaixo dele nomeia o
 * formato para ninguém ler o quadro errado.
 */

type View = 'gross' | 'net' | 'stableford';

export function GroupStandings({
  group,
  viewerId,
}: {
  group: GroupRound;
  viewerId: string;
}) {
  // "Cada formato tem um resultado diferente, não um rótulo diferente." Match play não tem total:
  // o resultado é o placar de buracos, então ele não passa pelo quadro de tacadas.
  if (group.format.scoring === 'match' && group.match) {
    return <MatchScoreboard group={group} match={group.match} viewerId={viewerId} />;
  }
  if (group.format.contest === 'teams') {
    return <TeamsTable group={group} viewerId={viewerId} />;
  }
  return <IndividualTable group={group} viewerId={viewerId} />;
}

function IndividualTable({ group, viewerId }: { group: GroupRound; viewerId: string }) {
  // Abre no que foi disputado; as outras vistas continuam disponíveis porque o mesmo cartão
  // responde três perguntas diferentes.
  const [view, setView] = useState<View>(
    group.format.scoring === 'stableford'
      ? 'stableford'
      : group.format.handicapMode === 'net'
        ? 'net'
        : 'gross',
  );

  const rows = sortFor(group.standings, view);
  // "Você" é quem está lendo, não o dono do cartão: um amigo abre o mesmo flight e não pode ver
  // o próprio rótulo na linha de outra pessoa.
  const youParticipantId =
    group.participants.find((p) => p.userId !== null && p.userId === viewerId)?.id ?? null;
  const you = group.standings.find((s) => s.participantId === youParticipantId);

  return (
    <>
      <div className={styles.viewPicker}>
        <PillGroup label={t('standings.howToRead')}>
          <Pill selected={view === 'gross'} onClick={() => setView('gross')}>
            {t('record.gross')}
          </Pill>
          <Pill selected={view === 'net'} onClick={() => setView('net')}>
            {t('record.net')}
          </Pill>
          <Pill selected={view === 'stableford'} onClick={() => setView('stableford')}>
            {t('record.stableford')}
          </Pill>
        </PillGroup>
        <p className={styles.formatLine}>{describeFormat(group)}</p>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.colPos} scope="col">
              <span className="visually-hidden">{t('standings.position')}</span>
            </th>
            <th scope="col">{t('standings.player')}</th>
            <th className={styles.colNum} scope="col">
              {t('standings.strokesShort')}
            </th>
            {view === 'net' && (
              <th className={styles.colNum} scope="col">
                {t('standings.hcpShort')}
              </th>
            )}
            <th className={styles.colNum} scope="col">
              {view === 'gross'
                ? t('standings.par')
                : view === 'net'
                  ? t('standings.net')
                  : t('standings.points')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const isYou = row.participantId === youParticipantId;
            return (
              <tr key={row.participantId} className={cx(isYou && styles.rowYou)}>
                <td className={styles.colPos}>{positionFor(rows, row, index, view)}</td>
                <td>
                  <span className={styles.name}>{isYou ? t('record.you') : row.displayName}</span>
                  {row.kind === 'guest' && (
                    <span className={styles.tag}>{t('record.noAccount')}</span>
                  )}
                  {row.handicapIndexAtStart !== null && (
                    <span className={styles.hcpNote}>
                      {t('standings.hcp', { value: fmtDecimal(row.handicapIndexAtStart) })}
                    </span>
                  )}
                </td>
                <td className={styles.colNum}>{row.gross}</td>
                {view === 'net' && (
                  <td className={cx(styles.colNum, styles.muted)}>
                    {row.net === null ? '—' : `−${row.strokesReceived}`}
                  </td>
                )}
                <td className={cx(styles.colNum, styles.result)}>{valueFor(row, view)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {you && <InsightCard sample={sampleFor(group)}>{closingLine(rows, you, view)}</InsightCard>}
    </>
  );
}

function sortFor(standings: PlayerStanding[], view: View): PlayerStanding[] {
  const rows = [...standings];
  if (view === 'stableford') return rows.sort((a, b) => b.stablefordPoints - a.stablefordPoints);
  if (view === 'gross') return rows.sort((a, b) => a.gross - b.gross);
  // Quem não tem handicap sai do net e vai para o fim — não vira primeiro por acidente.
  return rows.sort((a, b) => {
    if ((a.net === null) !== (b.net === null)) return a.net === null ? 1 : -1;
    return (a.net ?? 0) - (b.net ?? 0);
  });
}

function valueFor(row: PlayerStanding, view: View): string {
  if (view === 'stableford') return String(row.stablefordPoints);
  if (view === 'gross') return fmtToPar(row.toPar);
  return row.net === null ? '—' : String(row.net);
}

/**
 * A colocação é recalculada por vista: o quadro que o servidor devolveu foi ordenado pelo formato
 * disputado, e trocar para "Gross" sem recalcular mostraria o pódio da outra disputa.
 */
function positionFor(
  rows: PlayerStanding[],
  row: PlayerStanding,
  index: number,
  view: View,
): number {
  const previous = rows[index - 1];
  if (!previous) return 1;
  return valueFor(previous, view) === valueFor(row, view)
    ? positionFor(rows, previous, index - 1, view)
    : index + 1;
}

function describeFormat(group: GroupRound): string {
  return t('standings.formatIndividual', {
    players: group.participants.length,
    scoring:
      group.format.scoring === 'stableford' ? t('record.stableford') : t('record.strokePlay'),
    handicap:
      group.format.handicapMode === 'net' ? t('record.net') : t('record.group.grossLower'),
  });
}

function sampleFor(group: GroupRound): string {
  return t('standings.sample', {
    holes: group.holesPlayed,
    players: group.participants.length,
  });
}

/**
 * "No bruto você foi o segundo; no net, o terceiro." — o fecho em uma frase do 1t.
 *
 * A frase é lida como veredito, então precisa aguentar o caso chato: **empate na liderança**.
 * Declarar um vencedor onde dois fizeram o mesmo número seria a tela inventando um resultado.
 */
function closingLine(rows: PlayerStanding[], you: PlayerStanding, view: View): string {
  const viewOrder = sortFor(rows, view);
  const viewPlace = positionFor(viewOrder, you, indexOf(viewOrder, you), view);
  const leaders = viewOrder.filter((r) => valueFor(r, view) === valueFor(viewOrder[0]!, view));
  const youLead = leaders.some((r) => r.participantId === you.participantId);

  if (leaders.length > 1) {
    return youLead
      ? t('standings.tiedLeadYou', { count: leaders.length })
      : t('standings.tiedLead', { count: leaders.length, place: fmtOrdinal(viewPlace) });
  }

  const grossOrder = sortFor(rows, 'gross');
  const grossPlace = positionFor(grossOrder, you, indexOf(grossOrder, you), 'gross');

  if (view !== 'gross' && grossPlace !== viewPlace) {
    return t('standings.grossVsView', {
      grossPlace: fmtOrdinal(grossPlace),
      view: view === 'net' ? t('standings.inNet') : t('standings.inStableford'),
      viewPlace: fmtOrdinal(viewPlace),
    });
  }

  const winner = viewOrder[0];
  return youLead || !winner
    ? t('standings.youWon')
    : t('standings.otherWon', { name: winner.displayName, place: fmtOrdinal(viewPlace) });
}

function indexOf(rows: PlayerStanding[], row: PlayerStanding): number {
  return rows.findIndex((r) => r.participantId === row.participantId);
}

// ------------------------------------------------------------------ duplas

/**
 * Best ball em stroke play (canvas 1t): **a dupla é a linha**, e os jogadores ficam recuados
 * abaixo dela com quantos buracos cada um contou — "é o dado que responde 'quem carregou a
 * dupla', e o membro que contou menos aparece esmaecido".
 */
function TeamsTable({ group, viewerId }: { group: GroupRound; viewerId: string }) {
  const [view, setView] = useState<View>(
    group.format.scoring === 'stableford'
      ? 'stableford'
      : group.format.handicapMode === 'net'
        ? 'net'
        : 'gross',
  );

  const yourTeam = teamOfViewer(group, viewerId);
  const rows = sortTeams(group.teams, view);
  const scramble = group.format.teamFormat === 'scramble';

  return (
    <>
      <div className={styles.viewPicker}>
        <PillGroup label={t('standings.howToRead')}>
          <Pill selected={view === 'gross'} onClick={() => setView('gross')}>
            {t('record.gross')}
          </Pill>
          <Pill selected={view === 'net'} onClick={() => setView('net')}>
            {t('record.net')}
          </Pill>
          <Pill selected={view === 'stableford'} onClick={() => setView('stableford')}>
            {t('record.stableford')}
          </Pill>
        </PillGroup>
        <p className={styles.formatLine}>{describeTeamFormat(group)}</p>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.colPos} scope="col">
              <span className="visually-hidden">{t('standings.position')}</span>
            </th>
            <th scope="col">{t('standings.pair')}</th>
            <th className={styles.colNum} scope="col">
              {t('standings.strokesShort')}
            </th>
            <th className={styles.colNum} scope="col">
              {view === 'gross'
                ? t('standings.par')
                : view === 'net'
                  ? t('standings.net')
                  : t('standings.points')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const members = group.participants.filter((p) => p.team === row.team);
            const counted = members.map(
              (m) => group.standings.find((s) => s.participantId === m.id)?.holesCounted ?? null,
            );
            const leastIdx = indexOfLeast(counted);
            const isYours = row.team === yourTeam;
            return (
              <tr key={row.team} className={cx(isYours && styles.rowYou)}>
                <td className={styles.colPos}>{positionForTeam(rows, row, index, view)}</td>
                <td>
                  <span className={styles.name}>
                    {members
                      .map((m) => (m.kind === 'owner' ? t('record.you') : m.displayName))
                      .join(' + ')}
                  </span>
                  <span className={styles.hcpNote}>
                    {t('standings.hcp', { value: fmtDecimal(row.combinedCourseHandicap) })}
                  </span>
                  {/* Só no best ball: no scramble a bola é uma, então "quem contou" não existe. */}
                  {!scramble && (
                    <span className={styles.members}>
                      {members.map((m, i) => (
                        <span
                          key={m.id}
                          className={cx(styles.member, i === leastIdx && styles.memberFaded)}
                        >
                          {m.kind === 'owner' ? t('record.you') : m.displayName}
                          {counted[i] !== null &&
                            ` ${t('standings.countedHoles', { count: counted[i] as number })}`}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td className={styles.colNum}>{row.gross}</td>
                <td className={cx(styles.colNum, styles.result)}>{valueForTeam(row, view)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <InsightCard sample={sampleFor(group)}>{teamsClosingLine(group, rows, yourTeam, view)}</InsightCard>
    </>
  );
}

// -------------------------------------------------------------- match play

/**
 * Match play (canvas 1t): **não há total**. O resultado é o placar de buracos, e a grade mostra
 * quem levou cada um — com os que sobraram tracejados quando a partida fecha antes do fim.
 * "Estado que só existe aqui e precisa ser explícito, senão parece dado faltando."
 */
/**
 * O placar buraco a buraco. Exportado porque o desktop (canvas 1z) o usa como **bloco lateral**
 * mesmo numa volta de stroke play — "Match play · como ficaria" —, fora do quadro de tacadas.
 */
export function MatchScoreboard({
  group,
  match,
  viewerId,
  compact = false,
}: {
  group: GroupRound;
  match: MatchResult;
  viewerId: string;
  /** No bloco lateral do desktop o formato já foi dito ao lado — repeti-lo aqui seria eco. */
  compact?: boolean;
}) {
  const yourTeam = teamOfViewer(group, viewerId);
  const youLead = match.leader !== null && match.leader === yourTeam;
  const scramble = group.format.teamFormat === 'scramble';
  const notPlayed = match.holes.filter((h) => h.winner === null).map((h) => h.holeNumber);
  const closedEarly = match.decidedAtHole !== null && match.decidedAtHole < group.holesPlayed;

  return (
    <>
      {!compact && (
        <div className={styles.viewPicker}>
          <p className={styles.formatLine}>{describeTeamFormat(group)}</p>
        </div>
      )}

      <div className={styles.matchHead}>
        <div className={styles.matchLabel}>{t('standings.matchResult')}</div>
        <div className={styles.matchScore}>
          {match.lead === 0 ? t('standings.matchTied') : `${match.lead}↑`}
        </div>
        <div className={styles.matchVerdict}>
          {match.lead === 0
            ? t('standings.matchEndedTied')
            : youLead
              ? t('standings.yourPairWon')
              : t('standings.teamWon', { team: match.leader ?? '' })}
        </div>
        {match.decidedAtHole !== null && (
          <div className={styles.matchDetail}>
            {t('standings.matchDetail', {
              lead: match.lead,
              count: match.remaining,
              hole: match.decidedAtHole,
            })}
          </div>
        )}
      </div>

      {(['A', 'B'] as TeamId[]).map((team) => {
        const members = group.participants.filter((p) => p.team === team);
        if (members.length === 0) return null;
        const standing = group.teams.find((t) => t.team === team);
        const won = team === 'A' ? match.won : match.lost;
        const lost = team === 'A' ? match.lost : match.won;
        return (
          <div
            key={team}
            className={cx(styles.matchTeam, team === yourTeam && styles.matchTeamYou)}
          >
            <span className={styles.teamBadge}>{team}</span>
            <span className={styles.matchTeamBody}>
              <span className={styles.name}>
                {members
                  .map((m) => (m.kind === 'owner' ? t('record.you') : m.displayName))
                  .join(' + ')}
              </span>
              <span className={styles.hcpNote}>
                {t('standings.hcp', {
                  value: fmtDecimal(standing?.combinedCourseHandicap ?? null),
                })}
              </span>
              <span className={styles.hcpNote}>
                {t('standings.wonLostHalved', { won, lost, halved: match.halved })}
              </span>
            </span>
          </div>
        );
      })}

      <div className={styles.matchGrid}>
        {match.holes.map((h) => (
          <span
            key={h.holeNumber}
            className={cx(
              styles.matchHole,
              h.winner === 'A' && styles.matchHoleA,
              h.winner === 'B' && styles.matchHoleB,
              h.winner === 'halved' && styles.matchHoleHalved,
              h.winner === null && styles.matchHoleUnplayed,
            )}
          >
            <span className={styles.matchHoleNum}>{h.holeNumber}</span>
            <span className={styles.matchHoleMark}>
              {h.winner === 'halved' ? '=' : (h.winner ?? '')}
            </span>
          </span>
        ))}
      </div>

      <div className={styles.matchLegend}>
        <span>
          <i className={cx(styles.swatch, styles.matchHoleA)} />{' '}
          {t('standings.teamTook', { team: 'A' })}
        </span>
        <span>
          <i className={cx(styles.swatch, styles.matchHoleB)} />{' '}
          {t('standings.teamTook', { team: 'B' })}
        </span>
        <span>
          <i className={cx(styles.swatch, styles.matchHoleHalved)} /> {t('standings.halved')}
        </span>
        <span>
          <i className={cx(styles.swatch, styles.matchHoleUnplayed)} /> {t('standings.notPlayed')}
        </span>
      </div>

      {/*
        O insight fica nos dois tamanhos (canvas 1z): "a partida fechou no 16" é o que explica
        os buracos tracejados da grade, e sem ele eles parecem dado faltando. O que saiu da
        tela foi a explicação de que match play não tem total — essa a legenda carrega.
      */}
      <InsightCard sample={sampleFor(group)}>
        {closedEarly
          ? t('standings.closedEarly', {
              hole: match.decidedAtHole ?? '',
              holes: listHoles(notPlayed),
            })
          : t('standings.wentToTheEnd')}
        {scramble && t('standings.scrambleNoHandicap')}
      </InsightCard>
    </>
  );
}

// ------------------------------------------------------------------ helpers

function teamOfViewer(group: GroupRound, viewerId: string): TeamId | null {
  return group.participants.find((p) => p.userId !== null && p.userId === viewerId)?.team ?? null;
}

function sortTeams(teams: TeamStanding[], view: View): TeamStanding[] {
  const rows = [...teams];
  if (view === 'stableford') return rows.sort((a, b) => b.stablefordPoints - a.stablefordPoints);
  if (view === 'gross') return rows.sort((a, b) => a.gross - b.gross);
  return rows.sort((a, b) => {
    if ((a.net === null) !== (b.net === null)) return a.net === null ? 1 : -1;
    return (a.net ?? 0) - (b.net ?? 0);
  });
}

function valueForTeam(row: TeamStanding, view: View): string {
  if (view === 'stableford') return String(row.stablefordPoints);
  if (view === 'gross') return fmtToPar(row.toPar);
  return row.net === null ? '—' : String(row.net);
}

function positionForTeam(
  rows: TeamStanding[],
  row: TeamStanding,
  index: number,
  view: View,
): number {
  const previous = rows[index - 1];
  if (!previous) return 1;
  return valueForTeam(previous, view) === valueForTeam(row, view)
    ? positionForTeam(rows, previous, index - 1, view)
    : index + 1;
}

/** Qual dos dois contou menos — é ele que aparece esmaecido (canvas 1t). */
function indexOfLeast(counted: (number | null)[]): number {
  let idx = -1;
  let least = Number.POSITIVE_INFINITY;
  counted.forEach((value, i) => {
    if (value !== null && value < least) {
      least = value;
      idx = i;
    }
  });
  // Empate não esmaece ninguém: os dois carregaram igual.
  return counted.filter((v) => v === least).length > 1 ? -1 : idx;
}

function describeTeamFormat(group: GroupRound): string {
  return t('standings.formatTeams', {
    team:
      group.format.teamFormat === 'scramble' ? t('record.scramble') : t('record.bestBall'),
    scoring:
      group.format.scoring === 'match'
        ? t('record.matchPlay')
        : group.format.scoring === 'stableford'
          ? t('record.stableford')
          : t('record.strokePlay'),
    handicap:
      group.format.handicapMode === 'net' ? t('record.net') : t('record.group.grossLower'),
  });
}

function teamsClosingLine(
  group: GroupRound,
  rows: TeamStanding[],
  yourTeam: TeamId | null,
  view: View,
): string {
  const [first, second] = rows;
  if (!first || !second) return t('standings.teamsRound');
  const tied = valueForTeam(first, view) === valueForTeam(second, view);
  if (tied) return t('standings.teamsTied');

  const margin =
    view === 'stableford'
      ? t('standings.marginPoints', { count: first.stablefordPoints - second.stablefordPoints })
      : t('standings.marginStrokes', {
          count: Math.abs(
            (view === 'net' ? (first.net ?? 0) : first.gross) -
              (view === 'net' ? (second.net ?? 0) : second.gross),
          ),
        });

  const won =
    first.team === yourTeam
      ? t('standings.yourPairTook')
      : t('standings.teamTookIt', { team: first.team });
  if (group.format.teamFormat === 'scramble') {
    return t('standings.wonByScramble', { won, margin });
  }

  const yours = group.participants.filter((p) => p.team === yourTeam);
  // `isYou` vem do `kind`, e não da comparação do nome com "Você": o rótulo é traduzido, e
  // compará-lo com um literal quebraria em qualquer idioma que não fosse o do literal.
  const counted = yours.map((m) => ({
    isYou: m.kind === 'owner',
    name: m.kind === 'owner' ? t('record.you') : (m.displayName.split(/\s+/)[0] ?? m.displayName),
    holes: group.standings.find((s) => s.participantId === m.id)?.holesCounted ?? 0,
  }));
  const top = [...counted].sort((a, b) => b.holes - a.holes)[0];
  const other = counted.find((c) => c !== top);
  if (!top || !other) return t('standings.wonBy', { won, margin });

  return top.isYou
    ? t('standings.youCarried', {
        won,
        margin,
        yours: top.holes,
        other: other.name,
        theirs: other.holes,
      })
    : t('standings.theyCarried', {
        won,
        margin,
        name: top.name,
        yours: top.holes,
        theirs: other.holes,
      });
}

function listHoles(holes: number[]): string {
  if (holes.length === 0) return '';
  if (holes.length === 1) return String(holes[0]);
  return t('standings.listAnd', {
    list: holes.slice(0, -1).join(', '),
    last: holes[holes.length - 1] ?? '',
  });
}
